import React, { useEffect, useRef, useState } from 'react'
import type { LoadedFile } from '../audio/types'
import './FileList.css'

interface FileListProps {
  files: LoadedFile[]
  selectedIndex: number
  onSelect: (index: number) => void
  onExportAll: () => void
  onDrop: (files: FileList) => void
  onLoadClick: () => void
  onReset: () => void
}

export function FileList({ files, selectedIndex, onSelect, onExportAll, onDrop, onLoadClick, onReset }: FileListProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Scroll active item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const activeItem = listRef.current.children[selectedIndex] as HTMLElement
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [selectedIndex])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onDrop(e.dataTransfer.files)
    }
  }

  return (
    <div 
      className={`file-list-container ${isDragging ? 'file-list-container--dragging' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="file-list-header">
        <div className="file-list-title">Files ({files.length})</div>
        {files.length > 0 && (
          <button className="icon-button-small" onClick={onLoadClick} title="Add files">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
      
      <div className="file-list" ref={listRef}>
        {files.map((file, index) => {
          const isSelected = index === selectedIndex
          return (
            <div
              key={`${file.name}-${index}`}
              className={`file-item ${isSelected ? 'file-item--selected' : ''}`}
              onClick={() => onSelect(index)}
            >
              <div className="file-item__name" title={file.name}>
                {file.name}
              </div>
              <div className="file-item__meta">
                {file.duration.toFixed(2)}s • {file.sampleRate}Hz • {file.bitDepth ? `${file.bitDepth}bit` : '—'}
              </div>
            </div>
          )
        })}
        {files.length === 0 && (
          <div className="file-list-empty">
            <div className="empty-message">No files loaded</div>
            <button className="pill" onClick={onLoadClick}>Load Loops</button>
            <div className="empty-sub">or drag & drop here</div>
          </div>
        )}
      </div>

      <div className="file-list-footer">
        <button 
          className="export-all-button"
          onClick={onExportAll}
          disabled={files.length === 0}
        >
          Export All
        </button>
        <button 
          className="reset-button"
          onClick={onReset}
          disabled={files.length === 0}
          title="Unload all files and reset parameters"
        >
          Reset All
        </button>
        <div className="file-count-label">
          {files.length > 0 ? `${files.length} files ready` : ''}
        </div>
      </div>
    </div>
  )
}
